import type {
  QueryClient,
  UseQueryResult,
  UseMutationResult,
  DefaultError,
} from '@tanstack/react-query';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../clients';
import { queryKeys } from '../lib/query-keys';
import type {
  PlanOfStudySummary,
  PlanOfStudyDetail,
  PlanOfStudyRequirement,
  RawPlanOfStudyDetail,
  CreatePlanOfStudyPayload,
  CopyPlanOfStudyPayload,
  UpdatePlanOfStudyPayload,
} from '../types/plans-of-study';
import {
  getPlansOfStudyPath,
  getPlansOfStudyBasePath,
  getPlansOfStudyDetailPath,
  getDataIngestUpdatePosStudentPlansPath,
  getDataIngestDisableScheduledUpdatePosStudentPlansPath,
} from '../utils/api-paths';
import type {
  PlanOfStudy,
  RawPlanOfStudy,
  PlanOfStudyFilters,
  PlansOfStudyResponse,
} from '../types';
import { STATUS_FILTER_VALUES } from '../shared/filters/filters.type';
import planOfStudyDetailMock from '../mock-data/plan-of-study-detail.json';
import {
  getCpContextHighschoolId,
  isConsolidatedPermissionsEnabled,
  isPlanOfStudyWafDescriptionEncodingEnabled,
  isPosReadOnly,
} from '../utils/cp-context';
import { generateId } from '@course-planner/shared';
import {
  normalizeCheckedSchoolNidsForPublishAndUpdate,
  requireNonEmptySchoolNidsForPublishAndUpdate,
} from '../utils/plan-publish-and-update';
import { encodeWafSafeDescriptionsInRequestBody } from '../utils/waf-safe-description';
import type {
  PublishAndUpdatePlanVariables,
  PublishAndUpdatePlanResult,
  CancelScheduledUpdatePosResponse,
} from '../types/publish-and-update';
import { readScheduledPublishFromSession } from '../containers/PlanOfStudy/publish-schedule-copy';

type PlanOfStudyLiteResponse = PlanOfStudySummary[];

/** API school refs use string `nid`; drop invalid or empty values at runtime. */
function isSchoolNidRef(s: unknown): s is { nid: string } {
  if (s === null || typeof s !== 'object') {
    return false;
  }
  const nid = (s as { nid?: unknown }).nid;
  return typeof nid === 'string' && nid.trim() !== '';
}

function isMockMode(): boolean {
  return Boolean(import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === 'true');
}

interface PlansOfStudyListBody {
  schools: string[];
  search: string;
  status?: string;
  published?: string;
  classYearFrom: number | null;
  classYearTo: number | null;
  page: number;
  pageSize: number;
}

/**
 * NIDs for POST /plans-of-study/list `schools`. Uses the grid's published-schools filter when set.
 *
 * Flag OFF: scopes to embedded Succeed high school when present (legacy GET `school=` parity).
 * Flag ON: district manage users see all plans (`[]`); view-only school users scope to embed school
 * (aligned with {@link resolveClusterSchoolNids} in clusters-repository).
 */
export function resolveSchoolNidsForPlansOfStudyList(filters?: PlanOfStudyFilters): string[] {
  const fromFilter = filters?.publishedSchools ?? [];
  if (fromFilter.length > 0) {
    return fromFilter;
  }

  if (!isConsolidatedPermissionsEnabled()) {
    const context = getCpContextHighschoolId();
    return context ? [context] : [];
  }

  if (!isPosReadOnly()) {
    return [];
  }

  const schoolId = getCpContextHighschoolId()?.trim() ?? '';
  return schoolId.length > 0 ? [schoolId] : [];
}

function buildPostBody(filters?: PlanOfStudyFilters): PlansOfStudyListBody {
  return {
    schools: resolveSchoolNidsForPlansOfStudyList(filters),
    search: filters?.search ?? '',
    ...(filters?.hideInactive ? { status: STATUS_FILTER_VALUES.ACTIVE } : {}),
    ...(filters?.published ? { published: filters.published } : {}),
    classYearFrom: filters?.classYearFrom ? Number(filters.classYearFrom) : null,
    classYearTo: filters?.classYearTo ? Number(filters.classYearTo) : null,
    page: filters?.page ?? 1,
    pageSize: filters?.pageSize ?? 20,
  };
}

function transformPlan(raw: RawPlanOfStudy): PlanOfStudy {
  const publishedSchools = (Array.isArray(raw.publishedSchools) ? raw.publishedSchools : []).filter(
    isSchoolNidRef
  );
  const activeSchools = (Array.isArray(raw.activeSchools) ? raw.activeSchools : []).filter(
    isSchoolNidRef
  );

  const credits =
    raw.totalCredits ??
    (raw.requirements
      ? parseFloat(raw.requirements.reduce((sum, r) => sum + (r.credits || 0), 0).toFixed(2))
      : 0);

  let assignedClassYears = '';
  if (
    raw.classYearFrom !== undefined &&
    raw.classYearFrom !== null &&
    raw.classYearTo !== undefined &&
    raw.classYearTo !== null
  ) {
    assignedClassYears = `${raw.classYearFrom} - ${raw.classYearTo}`;
  }

  const isActive =
    raw.publishedVersion !== false && (raw.status ? raw.status.toLowerCase() !== 'inactive' : true);

  return {
    id: raw.id,
    name: raw.name ?? '',
    credits,
    description: raw.descriptionPlainText ?? raw.description ?? '',
    assignedClassYears,
    publishedSchools,
    activeSchools,
    isActive,
    isPublished: publishedSchools.length > 0,
    checkedToPublish: raw.checkedToPublish ?? false,
  };
}

/**
 * Transform backend plan detail (arrays + lastPublished timestamp) to UI PlanOfStudyDetail.
 */
function transformPlanDetail(raw: RawPlanOfStudyDetail): PlanOfStudyDetail {
  const publishedArr = (Array.isArray(raw.publishedSchools) ? raw.publishedSchools : []).filter(
    isSchoolNidRef
  );
  const activeArr = (Array.isArray(raw.activeSchools) ? raw.activeSchools : []).filter(
    isSchoolNidRef
  );
  const lastPublishedAt =
    raw.lastPublished !== null && raw.lastPublished !== undefined && raw.lastPublished > 0
      ? new Date(raw.lastPublished).toISOString()
      : null;
  const isPublished = raw.publishedVersion === true || publishedArr.length > 0;
  const currentYear = new Date().getFullYear();
  const fromYear = typeof raw.classYearFrom === 'number' ? raw.classYearFrom : currentYear;
  const toYear = typeof raw.classYearTo === 'number' ? raw.classYearTo : currentYear;

  const checkedArr = (Array.isArray(raw.checkedSchools) ? raw.checkedSchools : []).filter(
    isSchoolNidRef
  );

  return {
    id: raw.id ?? '',
    name: raw.name ?? '',
    description: raw.description,
    descriptionPlainText: raw.descriptionPlainText,
    classYearFrom: fromYear,
    classYearTo: toYear,
    groupRestrictions: raw.groupRestrictions,
    alternatesRequired: raw.alternatesRequired,
    oneToOneAlternative: raw.oneToOneAlternative,
    showGradeSetting: raw.showGradeSetting,
    gradeSettings: raw.gradeSettings,
    lastPublishedAt: lastPublishedAt ?? null,
    ...(raw.lastPublished !== undefined ? { lastPublishedMs: raw.lastPublished } : {}),
    ...(raw.lastSaved !== undefined ? { lastSavedMs: raw.lastSaved } : {}),
    publishedSchoolsCount: publishedArr.length,
    activeSchoolsCount: activeArr.length,
    publishedSchools: publishedArr.map((s) => ({ nid: s.nid })),
    activeSchools: activeArr.map((s) => ({ nid: s.nid })),
    isPublished: Boolean(isPublished),
    requirements: raw.requirements,
    guid: raw.guid,
    type: raw.type,
    fileVersionId: raw.fileVersionId,
    gradeSettingsField: raw.gradeSettingsField ?? undefined,
    publishedVersion: raw.publishedVersion,
    checkedToPublish: raw.checkedToPublish ?? false,
    checkedSchools: checkedArr.map((s) => ({ nid: s.nid })),
  };
}

/**
 * When running `npm run dev:mock` / `VITE_USE_MOCKS=true`, plans whose detail has no requirements
 * would only show “No requirements added…”. Inject lightweight rows so the Plan requirements table
 * matches typical QA data without calling the API.
 */
const MOCK_DEFAULT_PLAN_REQUIREMENTS: PlanOfStudyRequirement[] = [
  {
    id: 'mock-req-math',
    name: 'Mathematics',
    credits: 4,
    descriptionPlainText: 'Core mathematics credits (mock).',
  },
  {
    id: 'mock-req-english',
    name: 'English',
    credits: 4,
    descriptionPlainText: 'English language arts (mock).',
  },
  {
    id: 'mock-req-science',
    name: 'Science',
    credits: 3,
    descriptionPlainText: 'Laboratory science (mock).',
  },
];

function planHasRequirementsForPlanRequirementsSection(plan: PlanOfStudyDetail): boolean {
  const reqs = plan.requirements ?? [];
  if (reqs.length === 0) {
    return false;
  }
  return reqs.some((r) => r.alternates === true) || reqs.some((r) => !r.alternates);
}

function withMockPlanRequirementsWhenEmpty(plan: PlanOfStudyDetail): PlanOfStudyDetail {
  if (!isMockMode() || planHasRequirementsForPlanRequirementsSection(plan)) {
    return plan;
  }
  return {
    ...plan,
    requirements: [...MOCK_DEFAULT_PLAN_REQUIREMENTS],
  };
}

/**
 * In-memory detail for mock create/update/copy ids that are not in plan-of-study-detail.json.
 * Without this, usePlanOfStudyDetail falls back to a placeholder and loses the real plan name.
 */
const mockPlanDetailRegistry = new Map<string, PlanOfStudyDetail>();

function planDetailFromCreatePayload(
  id: string,
  payload: CreatePlanOfStudyPayload
): PlanOfStudyDetail {
  const base: PlanOfStudyDetail = {
    id,
    name: payload.name,
    description: payload.description,
    descriptionPlainText: payload.descriptionPlainText ?? '',
    classYearFrom: payload.classYearFrom,
    classYearTo: payload.classYearTo,
    groupRestrictions: payload.groupRestrictions ?? [],
    alternatesRequired: payload.alternatesRequired ?? false,
    oneToOneAlternative: payload.oneToOneAlternative ?? false,
    showGradeSetting: payload.showGradeSetting ?? false,
    gradeSettings: payload.gradeSettings ?? [],
    gradeSettingsField: payload.gradeSettingsField ?? JSON.stringify(payload.gradeSettings ?? []),
    lastPublishedAt: null,
    lastPublishedMs: null,
    lastSavedMs: null,
    publishedSchoolsCount: 0,
    activeSchoolsCount: 0,
    publishedSchools: [],
    activeSchools: [],
    isPublished: false,
    requirements: payload.requirements ?? [],
  };
  return withMockPlanRequirementsWhenEmpty(base);
}

function planDetailFromUpdatePayload(
  payload: { id: string } & UpdatePlanOfStudyPayload
): PlanOfStudyDetail {
  const { id, isPosChanged: _ignored, ...createFields } = payload;
  return planDetailFromCreatePayload(id, createFields);
}

/** Sync lookup for mock copy when detail query cache is empty (e.g. user copied from list only). */
function getMockPlanDetailFromJsonById(id: string): PlanOfStudyDetail | null {
  const raw = (planOfStudyDetailMock as Record<string, RawPlanOfStudyDetail | undefined>)[id];
  return raw ? withMockPlanRequirementsWhenEmpty(transformPlanDetail(raw)) : null;
}

type InvalidatePlansOfStudyDetailOptions = {
  /**
   * Refetch this plan's detail (active or inactive). Omit when only list data changed (create/copy)
   * or the server did not change an existing plan's GET-by-id payload.
   */
  detailPlanId?: string;
  /** Drop this plan's detail cache (e.g. after delete). */
  removeDetailPlanId?: string;
};

/**
 * Refresh list-lite and every cached `useGetPlansOfStudy` filter (refetchType `all`, including inactive),
 * so grids stay correct after mutations. Optionally invalidate or remove **one** plan detail so other
 * cached plans keep `staleTime: Infinity` and do not GET again on breadcrumb navigation.
 *
 * If new `queryKeys.plansOfStudy.*` variants are added, ensure mutations either invalidate them here
 * or call `invalidateQueries`/`removeQueries` alongside this helper so nothing stays stale indefinitely.
 */
/**
 * Build PUT …/publish body from raw plan detail + selected schools (shared by publish-only).
 */
function buildPlanPublishPayloadForPut(raw: RawPlanOfStudyDetail, checkedSchoolNids: string[]) {
  const { requirements: _requirements, ...planWithoutRequirements } = raw;
  const rawPublishPayload = {
    ...planWithoutRequirements,
    checkedToPublish: true,
    checkedSchools: checkedSchoolNids.map((nid) => ({ nid })),
  };
  return isPlanOfStudyWafDescriptionEncodingEnabled()
    ? encodeWafSafeDescriptionsInRequestBody(rawPublishPayload)
    : rawPublishPayload;
}

function invalidatePlansOfStudyAfterMutation(
  queryClient: QueryClient,
  options?: InvalidatePlansOfStudyDetailOptions
): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.plansOfStudy.listLite(),
    refetchType: 'all',
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.plansOfStudy.lists(),
    refetchType: 'all',
  });

  if (options?.removeDetailPlanId) {
    queryClient.removeQueries({
      queryKey: queryKeys.plansOfStudy.detail(options.removeDetailPlanId),
    });
  }
  if (options?.detailPlanId) {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.plansOfStudy.detail(options.detailPlanId),
      refetchType: 'all',
    });
  }
}

/**
 * Repository for Plans of study (staff): list lite, create, copy.
 * Uses existing course-planner-service APIs via getApiBaseUrl / api-paths.
 * When VITE_USE_MOCKS=true in dev, uses mock data so the UI can be viewed without the backend.
 *
 * **Publish + schedule:** `usePublishAndUpdatePlanOfStudy` uses `PUT …/publish` + data-ingest for
 * immediate runs; scheduled runs omit PUT and pass `deferredSchoolPublish` so school publish runs
 * when the data-ingest job fires (import processor → Academic Planner school publish). Cancel
 * uses `POST …/update-pos-student-plans/disable` with that `scheduleId`.
 */
export const plansOfStudyRepository = {
  /**
   * Fetch all plans of study (lite) for dropdown and list.
   */
  usePlansOfStudyListLite(enabled = true): UseQueryResult<PlanOfStudySummary[], DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.plansOfStudy.listLite(),
      queryFn: async () => {
        if (isMockMode()) {
          const mockData = await import('../mock-data/plans-of-study-lite.json');
          return (mockData.default ?? mockData) as PlanOfStudySummary[];
        }
        const path = getPlansOfStudyPath('all-lite');
        const response = await apiClient.get<PlanOfStudyLiteResponse>(path);
        return response.data;
      },
      enabled,
    });
  },

  /**
   * Create a new Plan of Study.
   */
  useCreatePlanOfStudy(): UseMutationResult<
    { id: string; name: string },
    DefaultError,
    CreatePlanOfStudyPayload
  > {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: CreatePlanOfStudyPayload) => {
        if (isMockMode()) {
          const id = `pos-mock-${Date.now()}`;
          return { id, name: payload.name };
        }
        const path = getPlansOfStudyBasePath();
        const rawBody = {
          ...payload,
          requirements: payload.requirements ?? [],
        };
        const body = isPlanOfStudyWafDescriptionEncodingEnabled()
          ? encodeWafSafeDescriptionsInRequestBody(rawBody)
          : rawBody;
        const response = await apiClient.post<{ id: string; name: string }>(path, body);
        return response.data;
      },
      onSuccess: (data, variables) => {
        if (isMockMode()) {
          mockPlanDetailRegistry.set(data.id, planDetailFromCreatePayload(data.id, variables));
        }
        invalidatePlansOfStudyAfterMutation(queryClient);
      },
    });
  },

  /**
   * Copy an existing Plan of Study.
   */
  useCopyPlanOfStudy(): UseMutationResult<
    { id: string; name: string },
    DefaultError,
    CopyPlanOfStudyPayload
  > {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: CopyPlanOfStudyPayload) => {
        if (isMockMode()) {
          const id = `pos-mock-copy-${Date.now()}`;
          return { id, name: payload.name };
        }
        const path = getPlansOfStudyPath('copy');
        const response = await apiClient.post<{ id: string; name: string }>(path, payload);
        return response.data;
      },
      onSuccess: (data, variables) => {
        if (isMockMode()) {
          const fromCache = queryClient.getQueryData<PlanOfStudyDetail | null>(
            queryKeys.plansOfStudy.detail(variables.id)
          );
          const fromJson = fromCache ?? getMockPlanDetailFromJsonById(variables.id);
          const currentYear = new Date().getFullYear();

          if (fromJson) {
            mockPlanDetailRegistry.set(data.id, {
              ...fromJson,
              id: data.id,
              name: variables.name,
              lastPublishedAt: null,
              isPublished: false,
              publishedSchoolsCount: 0,
              activeSchoolsCount: 0,
              publishedSchools: [],
              activeSchools: [],
            });
          } else {
            mockPlanDetailRegistry.set(
              data.id,
              planDetailFromCreatePayload(data.id, {
                name: variables.name,
                classYearFrom: currentYear,
                classYearTo: currentYear,
                requirements: [],
              })
            );
          }
        }
        invalidatePlansOfStudyAfterMutation(queryClient);
      },
    });
  },

  /**
   * Fetch a single plan of study by id (for detail and edit pages).
   *
   * Caching: `staleTime: Infinity` avoids refetching on every navigation between sibling routes
   * (plan detail ↔ requirement detail ↔ create requirement) that each subscribe to this query.
   * Plan data refetches when a mutation invalidates **that plan's** detail (update/publish/active schools)
   * or list queries (any mutation). Other plans' cached details stay fresh (`staleTime: Infinity`) so
   * breadcrumb back-navigation does not GET them again. Longer `gcTime` retains cache after unmount.
   */
  usePlanOfStudyDetail(
    id: string | undefined,
    enabled = true
  ): UseQueryResult<PlanOfStudyDetail | null, DefaultError> {
    const apiClient = useApiClient();

    return useQuery({
      queryKey: queryKeys.plansOfStudy.detail(id ?? ''),
      queryFn: async (): Promise<PlanOfStudyDetail | null> => {
        if (!id) {
          return null;
        }
        if (isMockMode()) {
          const mockData = await import('../mock-data/plan-of-study-detail.json');
          const byId = (mockData.default ?? mockData) as Record<string, RawPlanOfStudyDetail>;
          const raw = byId[id];
          if (raw) {
            return withMockPlanRequirementsWhenEmpty(transformPlanDetail(raw));
          }
          const registered = mockPlanDetailRegistry.get(id);
          if (registered) {
            return withMockPlanRequirementsWhenEmpty(registered);
          }
          return withMockPlanRequirementsWhenEmpty({
            id,
            name: '',
            descriptionPlainText: '',
            classYearFrom: new Date().getFullYear(),
            classYearTo: new Date().getFullYear(),
            groupRestrictions: [],
            alternatesRequired: false,
            oneToOneAlternative: false,
            showGradeSetting: false,
            gradeSettings: [],
            lastPublishedAt: null,
            publishedSchoolsCount: 0,
            activeSchoolsCount: 0,
            publishedSchools: [],
            activeSchools: [],
            isPublished: false,
            requirements: [],
          });
        }
        const path = getPlansOfStudyDetailPath(id);
        const response = await apiClient.get<RawPlanOfStudyDetail>(path);
        return transformPlanDetail(response.data);
      },
      enabled: Boolean(id) && enabled,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 60, // 1 hour — retain cache after unmount for back-navigation
    });
  },

  /**
   * Update an existing Plan of Study.
   */
  useUpdatePlanOfStudy(): UseMutationResult<
    { id: string; name: string },
    DefaultError,
    { id: string } & UpdatePlanOfStudyPayload
  > {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: { id: string } & UpdatePlanOfStudyPayload) => {
        const { id, ...rest } = payload;
        if (isMockMode()) {
          return { id, name: rest.name };
        }
        // CPUI and backend use PUT to base path with id in body (PlansOfStudyController @Put('/')).
        const path = getPlansOfStudyBasePath();
        const rawBody = {
          id,
          ...rest,
          requirements: rest.requirements ?? [],
          isPosChanged: rest.isPosChanged ?? true,
        };
        const body = isPlanOfStudyWafDescriptionEncodingEnabled()
          ? encodeWafSafeDescriptionsInRequestBody(rawBody)
          : rawBody;
        const response = await apiClient.put<{ id: string; name: string }>(path, body);
        return response.data;
      },
      onSuccess: (_, variables) => {
        if (isMockMode()) {
          mockPlanDetailRegistry.set(variables.id, planDetailFromUpdatePayload(variables));
        }
        invalidatePlansOfStudyAfterMutation(queryClient, { detailPlanId: variables.id });
      },
    });
  },

  /**
   * Publish a Plan of Study.
   * Mirrors the legacy saga: PUT /course-planner/plans-of-study/publish
   * Fetches the full raw plan, strips requirements, adds checkedSchools
   * (the schools the user selected in the drawer) and checkedToPublish.
   */
  usePublishPlanOfStudy(): UseMutationResult<
    void,
    DefaultError,
    { planId: string; checkedSchoolNids: string[] }
  > {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ planId, checkedSchoolNids }) => {
        if (isMockMode()) {
          return;
        }
        // Fetch the full raw plan (same shape the backend originally stored)
        const detailPath = getPlansOfStudyDetailPath(planId);
        const detailResponse = await apiClient.get<RawPlanOfStudyDetail>(detailPath);
        const raw = detailResponse.data;
        const publishPayload = buildPlanPublishPayloadForPut(raw, checkedSchoolNids);

        const publishPath = getPlansOfStudyPath('publish');
        await apiClient.put(publishPath, publishPayload);
      },
      onSuccess: (_data, variables) => {
        invalidatePlansOfStudyAfterMutation(queryClient, { detailPlanId: variables.planId });
      },
    });
  },

  /**
   * Publish and update student plans: staff uses `PUT …/publish` then `POST` data-ingest
   * `update-pos-student-plans`. When `mode === 'SCHEDULED'`, only registers the data-ingest job with
   * `deferredSchoolPublish`; school publish runs at schedule time inside the import processor
   * (Academic Planner school publish in import-export) before student propagation.
   */
  usePublishAndUpdatePlanOfStudy(): UseMutationResult<
    PublishAndUpdatePlanResult | undefined,
    DefaultError,
    PublishAndUpdatePlanVariables
  > {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (variables) => {
        const { planId, checkedSchoolNids, mode = 'IMMEDIATE', scheduledAt } = variables;

        const schoolNids = normalizeCheckedSchoolNidsForPublishAndUpdate(checkedSchoolNids ?? []);

        if (mode === 'SCHEDULED' && !scheduledAt?.trim()) {
          throw new Error('scheduledAt is required when mode is SCHEDULED');
        }

        if (isMockMode()) {
          if (mode === 'SCHEDULED' && scheduledAt?.trim()) {
            return {
              status: 'SCHEDULED' as const,
              scheduleId: 'mock-schedule-id',
              scheduledAt: scheduledAt.trim(),
            };
          }
          return { status: 'QUEUED' as const };
        }

        const ingestPath = getDataIngestUpdatePosStudentPlansPath();

        if (mode === 'SCHEDULED' && scheduledAt?.trim()) {
          const disablePath = getDataIngestDisableScheduledUpdatePosStudentPlansPath();
          const priorScheduled = readScheduledPublishFromSession(planId);
          if (priorScheduled?.scheduleId?.trim()) {
            await apiClient.post<CancelScheduledUpdatePosResponse>(disablePath, {
              scheduleId: priorScheduled.scheduleId.trim(),
            });
          }

          const scheduleId = generateId();
          const at = scheduledAt.trim();
          const detailPath = getPlansOfStudyDetailPath(planId);
          const detailResponse = await apiClient.get<RawPlanOfStudyDetail>(detailPath);
          const raw = detailResponse.data;
          const schoolList =
            schoolNids.length > 0
              ? schoolNids
              : (Array.isArray(raw.checkedSchools) ? raw.checkedSchools : [])
                  .filter(isSchoolNidRef)
                  .map((s) => s.nid);
          const schoolsForJob = requireNonEmptySchoolNidsForPublishAndUpdate(schoolList);
          const planOfStudyName = typeof raw.name === 'string' ? raw.name : '';
          const fileVersionId = raw.fileVersionId;
          if (typeof fileVersionId !== 'string' || fileVersionId.trim() === '') {
            throw new Error(
              'fileVersionId missing on plan; cannot enqueue scheduled update-pos-student-plans'
            );
          }
          await apiClient.post(ingestPath, {
            payload: {
              planId,
              schools: schoolsForJob,
              planOfStudyName,
              fileVersionId,
              scheduledAt: at,
              scheduleId,
              deferredSchoolPublish: true,
            },
          });
          return { status: 'SCHEDULED' as const, scheduleId, scheduledAt: at };
        }

        const detailPath = getPlansOfStudyDetailPath(planId);
        const detailResponse = await apiClient.get<RawPlanOfStudyDetail>(detailPath);
        const raw = detailResponse.data;
        const publishPayload = buildPlanPublishPayloadForPut(raw, schoolNids);
        const publishPath = getPlansOfStudyPath('publish');
        const putResponse = await apiClient.put<RawPlanOfStudyDetail>(publishPath, publishPayload);
        const published = putResponse.data;

        const checkedFromPublished = (
          Array.isArray(published.checkedSchools) ? published.checkedSchools : []
        ).filter(isSchoolNidRef);
        const schools = schoolNids.length > 0 ? schoolNids : checkedFromPublished.map((s) => s.nid);
        const schoolsForJob = requireNonEmptySchoolNidsForPublishAndUpdate(schools);

        const planOfStudyName = typeof published.name === 'string' ? published.name : '';
        const fileVersionId = published.fileVersionId;
        if (typeof fileVersionId !== 'string' || fileVersionId.trim() === '') {
          throw new Error(
            'fileVersionId missing after publish; cannot enqueue update-pos-student-plans'
          );
        }

        await apiClient.post(ingestPath, {
          payload: {
            planId,
            schools: schoolsForJob,
            planOfStudyName,
            fileVersionId,
          },
        });
        return { status: 'QUEUED' as const };
      },
      onSuccess: (_data, variables) => {
        invalidatePlansOfStudyAfterMutation(queryClient, { detailPlanId: variables.planId });
      },
    });
  },

  /**
   * Cancel scheduled `update-pos-student-plans` ingress (staff “Publish later”); same `scheduleId`
   * as the enqueue POST.
   */
  useCancelScheduledUpdatePosStudentPlans(): UseMutationResult<
    CancelScheduledUpdatePosResponse,
    DefaultError,
    { scheduleId: string }
  > {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ scheduleId }) => {
        if (isMockMode()) {
          return { ok: true as const };
        }
        const path = getDataIngestDisableScheduledUpdatePosStudentPlansPath();
        const res = await apiClient.post<CancelScheduledUpdatePosResponse>(path, { scheduleId });
        return res.data;
      },
      onSuccess: () => {
        invalidatePlansOfStudyAfterMutation(queryClient);
      },
    });
  },

  /**
   * Update active schools for a Plan of Study.
   * PUT /plans-of-study/active-schools with { id, activeSchools: [{ nid }] }.
   */
  useUpdateActiveSchools(): UseMutationResult<
    void,
    DefaultError,
    { planId: string; activeSchoolNids: string[] }
  > {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ planId, activeSchoolNids }) => {
        if (isMockMode()) {
          return;
        }
        const path = getPlansOfStudyPath('active-schools');
        await apiClient.put(path, {
          id: planId,
          activeSchools: activeSchoolNids.map((nid) => ({ nid })),
        });
      },
      onSuccess: (_data, variables) => {
        invalidatePlansOfStudyAfterMutation(queryClient, { detailPlanId: variables.planId });
      },
    });
  },

  /**
   * Delete a Plan of Study (only when not published).
   */
  useDeletePlanOfStudy(): UseMutationResult<void, DefaultError, string> {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (id: string) => {
        if (isMockMode()) {
          return;
        }
        const path = getPlansOfStudyDetailPath(id);
        await apiClient.delete(path);
      },
      onSuccess: (_data, deletedPlanId) => {
        invalidatePlansOfStudyAfterMutation(queryClient, { removeDetailPlanId: deletedPlanId });
      },
    });
  },
};

/**
 * Paginated/filtered grid for the plans-of-study list page. Each `filters` object is its own cache entry.
 * After mutations, list queries refetch (`refetchType: 'all'`) so grids show updated rows; only the
 * affected plan's detail is invalidated so other cached plan details are not refetched unnecessarily.
 */
export type {
  PublishAndUpdatePlanVariables,
  PublishAndUpdatePlanResult,
} from '../types/publish-and-update';

export function useGetPlansOfStudy(
  filters?: PlanOfStudyFilters,
  enabled = true
): UseQueryResult<PlansOfStudyResponse, DefaultError> {
  const apiClient = useApiClient();
  const listPath = getPlansOfStudyPath('list');

  return useQuery({
    queryKey: queryKeys.plansOfStudy.list(filters),
    enabled,
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<PlansOfStudyResponse> => {
      if (isMockMode()) {
        const mockData = await import('../mock-data/plans-of-study.json');
        let list: PlanOfStudy[] = [...(mockData.default.data as PlanOfStudy[])];

        if (filters?.hideInactive) {
          list = list.filter((p) => p.isActive);
        }
        if (filters?.status && filters.status !== 'all') {
          const isActive = filters.status.toLowerCase() === 'active';
          list = list.filter((p) => p.isActive === isActive);
        }
        if (filters?.search) {
          const term = filters.search.toLowerCase();
          list = list.filter(
            (p) => p.name.toLowerCase().includes(term) || p.description.toLowerCase().includes(term)
          );
        }

        const page = filters?.page ?? 1;
        const pageSize = filters?.pageSize ?? 20;
        const total = list.length;
        const totalPages = Math.max(1, Math.ceil(total / pageSize));
        const start = (page - 1) * pageSize;
        const data = list.slice(start, start + pageSize);

        return { data, total, currentPage: page, totalPages, pageSize };
      }

      const postBody = buildPostBody(filters);
      const response = await apiClient.post(listPath, postBody);
      const body = response.data;

      const rawPlans: RawPlanOfStudy[] = Array.isArray(body.data)
        ? body.data
        : Array.isArray(body)
          ? body
          : [];
      const data = rawPlans.map(transformPlan);

      const total = body.total ?? data.length;
      const currentPage = body.currentPage ?? filters?.page ?? 1;
      const pageSize = body.pageSize ?? filters?.pageSize ?? 20;
      const totalPages = body.totalPages ?? Math.max(1, Math.ceil(total / pageSize));

      return { data, total, currentPage, totalPages, pageSize };
    },
    retry: false,
    throwOnError: false,
  });
}
